import { createContext, type PropsWithChildren, useContext, useMemo } from 'react';

import { useOrganization } from '@/features/members/api/organization-api';

interface OrgTimezoneValue {
  /** False when we are falling back to the viewer's own browser zone. */
  isOrgConfigured: boolean;
  /** IANA zone in effect for this viewer. Never empty. */
  timeZone: string;
}

const OrgTimezoneContext = createContext<OrgTimezoneValue | null>(null);

const browserTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Whether `Intl`/`TZDate` can actually resolve this zone in the browser.
 *
 * The backend validates stored timezones against Python's
 * `zoneinfo.available_timezones()`, which is a superset of what browsers
 * implement — e.g. `Factory` passes backend validation but throws
 * `RangeError` from `Intl.DateTimeFormat` and from `TZDate`. Since every
 * date render in the app goes through one of those two, an unusable stored
 * zone would otherwise throw on first render with no error boundary to
 * catch it, blanking every route for the whole org.
 */
const isUsableTimeZone = (tz: string): boolean => {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
};

interface OrgTimezoneProviderProps extends PropsWithChildren {
  /**
   * Overrides the organization query. Used by the public-share routes, which
   * are unauthenticated and receive the zone in the chart payload instead.
   */
  timeZone?: string;
}

/**
 * Resolves the timezone every date render should use.
 *
 * Falls back to the browser zone whenever the organization has not set one —
 * including while the query is in flight — so first paint never shows a date
 * that visibly corrects itself a moment later, and a failed org query never
 * blocks rendering. Also falls back when the stored/passed zone is one the
 * browser cannot resolve (see {@link isUsableTimeZone}), since rendering with
 * an unusable zone would throw rather than merely look wrong.
 */
export function OrgTimezoneProvider({ timeZone, children }: OrgTimezoneProviderProps) {
  const { data: org } = useOrganization();

  const value = useMemo<OrgTimezoneValue>(() => {
    const candidate = timeZone ?? org?.timezone ?? null;
    const resolved = candidate !== null && isUsableTimeZone(candidate) ? candidate : null;
    return {
      // An org that set a zone the browser can't resolve is configured, just
      // broken — that's a distinct state from "never set one", but we don't
      // have a third UI treatment for it, and silently reporting "not set"
      // here would make the settings page invite the user to "fix" a value
      // that already looks unset. Surfacing it as configured (with a working
      // browser-zone fallback for rendering) is the safer of two imperfect
      // options: it keeps the app from white-screening while still pointing
      // an admin at the settings page to correct the actual bad value.
      isOrgConfigured: candidate !== null,
      timeZone: resolved ?? browserTimeZone(),
    };
  }, [timeZone, org?.timezone]);

  return <OrgTimezoneContext.Provider value={value}>{children}</OrgTimezoneContext.Provider>;
}

export function useOrgTimezone(): OrgTimezoneValue {
  const value = useContext(OrgTimezoneContext);
  if (!value) throw new Error('useOrgTimezone must be used within an OrgTimezoneProvider');
  return value;
}
